import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { permissionsAPI } from '../lib/api';
import { useAuth } from './AuthContext';

const PermissionsContext = createContext(null);

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

export const PermissionsProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const loadPermissions = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setPermissions({});
      setIsLoading(false);
      return;
    }

    try {
      const response = await permissionsAPI.getMyPermissions();
      setPermissions(response.data || {});
    } catch (error) {
      console.error('Error loading permissions:', error);
      // Fallback to empty permissions on error
      setPermissions({});
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  // Check if user has a specific permission
  const hasPermission = useCallback((module, action) => {
    // Admin always has all permissions
    if (user?.ruolo === 'admin') return true;
    
    return permissions[module]?.[action] === true;
  }, [permissions, user]);

  // Check if user has any permission in a module
  const hasModuleAccess = useCallback((module) => {
    // Admin always has access
    if (user?.ruolo === 'admin') return true;
    
    const modulePerms = permissions[module];
    if (!modulePerms) return false;
    
    return Object.values(modulePerms).some(v => v === true);
  }, [permissions, user]);

  // Check multiple permissions (AND logic)
  const hasAllPermissions = useCallback((checks) => {
    return checks.every(({ module, action }) => hasPermission(module, action));
  }, [hasPermission]);

  // Check multiple permissions (OR logic)
  const hasAnyPermission = useCallback((checks) => {
    return checks.some(({ module, action }) => hasPermission(module, action));
  }, [hasPermission]);

  // Refresh permissions (useful after admin updates)
  const refreshPermissions = useCallback(() => {
    setIsLoading(true);
    loadPermissions();
  }, [loadPermissions]);

  const value = {
    permissions,
    isLoading,
    hasPermission,
    hasModuleAccess,
    hasAllPermissions,
    hasAnyPermission,
    refreshPermissions
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export default PermissionsContext;
